import { TellescopeModelFromName } from "../../mappings";

const DEFAULT_INBOX_STATUS = "New";

export const EXCLUDED_INBOX_THREAD_KEYS_FROM_CHAT = [
  "title",
  "archivedAt",
  "emailMessageId",
  "enduserPhoneNumber",
  "phoneNumber",
  "trashedAt",
  "scheduledMessageIds",
  "draftMessageIds",
  "id",
  "tags",
  "createdAt",
  "sharedWithOrganizations",
  "originalId",
  "userIds",
  "assignedTo",
  "businessId",
  "enduserIds",
  "creator",
  "updatedAt",
  "organizationIds",
  "isDraftOnlyThread",
  "searchKeywords",
] as const satisfies ExcludedInboxThreadKeys[];

type PartialInboxThread = Pick<
  TellescopeModelFromName<"inbox_threads">,
  | "type"
  | "threadId"
  | "preview"
  | "timestamp"
  | "inboxStatus"
  | "readBy"
  | "outboundPreview"
  | "outboundTimestamp"
  | "recentOutboundUserId"
  | "recentInboundEnduserId"
>;
type ExcludedInboxThreadKeys = Exclude<keyof TellescopeModelFromName<"inbox_threads">, keyof PartialInboxThread>;

export const removeChatExcludedInboxThreadFields = <T extends Partial<TellescopeModelFromName<"inbox_threads">>>(
  inboxThread: T
): PartialInboxThread => {
  const entries = Object.entries(inboxThread);
  return Object.fromEntries(
    entries.filter(([key]) => !EXCLUDED_INBOX_THREAD_KEYS_FROM_CHAT.includes(key as ExcludedInboxThreadKeys))
  ) as PartialInboxThread;
};

/**
 * Converts a ChatMessage to a partial InboxThread representation.
 *
 * IMPORTANT LIMITATIONS:
 * - ChatMessages do NOT contain full thread metadata (assignedTo, userIds, enduserIds, title, etc.)
 * - The ChatRoom is the actual source of thread information for Chat type
 * - This utility derives what it can from the ChatMessage alone
 * - For complete thread information, use convert-chat-room-to-inbox-thread.ts instead
 *
 * Purpose: Used for cache invalidation when a new ChatMessage is created but you
 * don't have the full ChatRoom data. This helps determine if the message could
 * affect cached inbox_threads based on timestamp, preview, and threadId (roomId).
 *
 * Use case example:
 * - A new chat message arrives via webhook
 * - You want to quickly check if it affects cached threads without fetching the full room
 * - You can derive basic thread properties from the message itself
 *
 * For full accuracy, you should fetch the ChatRoom and use convertChatRoomToInboxThread.
 *
 * @param message - The ChatMessage to convert
 * @returns A partial InboxThread representation derived from the message
 */
export const convertChatMessageToInboxThread = (
  message: TellescopeModelFromName<"chats">
): Pick<
  TellescopeModelFromName<"inbox_threads">,
  | "type"
  | "threadId"
  | "preview"
  | "timestamp"
  | "inboxStatus"
  | "readBy"
  | "outboundPreview"
  | "outboundTimestamp"
  | "recentOutboundUserId"
  | "recentInboundEnduserId"
> => {
  const now = new Date();

  // Determine if this is a draft or scheduled message
  const isDraft = message.isDraft;
  const isScheduled = message.sendAt && new Date(message.sendAt) > now;
  const useForPreview = !isDraft && !isScheduled;

  // Determine if sender is an enduser (messages from endusers appear as "inbound" in threads)
  const isFromEnduser = !!message.enduserId;
  const isFromUser = !!message.userId;

  // Build basic readBy - if message has readBy, use it
  const readBy = message.readBy || {};

  return {
    type: "Chat",
    // Use roomId as threadId (Chat threads are based on rooms)
    threadId: message.roomId,
    // Preview is the message content if from enduser and not draft/scheduled
    preview: isFromEnduser && useForPreview ? message.message : "",
    // Timestamp from the message
    timestamp: new Date(message.createdAt || new Date()),
    // Default inbox status (we don't have room's inboxStatus from message alone)
    inboxStatus: DEFAULT_INBOX_STATUS,
    readBy,
    // Outbound preview if message is from a user (not enduser)
    outboundPreview: isFromUser && useForPreview ? message.message : "",
    // Outbound timestamp if from user
    outboundTimestamp: isFromUser ? new Date(message.createdAt || new Date()) : undefined,
    // Recent sender IDs based on who sent the message
    ...(isFromUser && message.userId ? { recentOutboundUserId: message.userId } : {}),
    ...(isFromEnduser && message.enduserId ? { recentInboundEnduserId: message.enduserId } : {}),
  };
};
